import { Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTasks } from '../../api/queries';
import { useUI } from '../../app/ui';
import { Empty, ErrorState, Modal, Spinner } from '../../components/ui';
import { useDebounced } from '../../lib/hooks';
import { PriorityBadge, TaskMeta } from './bits';

/** Global search (S). Debounced, typo-tolerant (server trigram search), includes closed tasks. */
export function SearchPalette() {
  const ui = useUI();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim(), 200);
  const res = useTasks({ view: 'all', q: dq, includeClosed: true, sort: 'updated', dir: 'desc', limit: 20 }, ui.searchOpen && dq.length > 0);
  const items = dq ? (res.data?.items ?? []) : [];
  const close = () => {
    ui.setSearchOpen(false);
    setQ('');
    setActive(0);
  };
  const open = (id: string) => {
    close();
    nav(`/tasks/${id}`);
  };
  return (
    <Modal open={ui.searchOpen} onClose={close} title="Search tasks" initialFocus="#global-search">
      <div className="search-box mb" style={{ maxWidth: 'none' }}>
        <Search size={16} aria-hidden />
        <input
          id="global-search"
          className="input"
          value={q}
          placeholder="Search by title, ref (T-1042), tag, person, project…"
          aria-label="Search tasks"
          aria-controls="search-results"
          aria-activedescendant={items[active] ? `sr-${items[active]!.id}` : undefined}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(items.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === 'Enter' && items[active]) {
              e.preventDefault();
              open(items[active]!.id);
            }
          }}
        />
      </div>
      {!dq ? (
        <p className="muted small">Type to search. Partial words and small typos are fine.</p>
      ) : res.isError ? (
        <ErrorState error={res.error} onRetry={() => res.refetch()} />
      ) : res.isPending || (res.isFetching && !res.data) ? (
        <Spinner label="Searching" />
      ) : !items.length ? (
        <Empty icon="🔎" title="No results">
          Nothing matches “{dq}”.
        </Empty>
      ) : (
        <div className="list" id="search-results" role="listbox" aria-label="Search results">
          {items.map((t, i) => (
            <div
              key={t.id}
              id={`sr-${t.id}`}
              role="option"
              aria-selected={i === active}
              className={`row ${i === active ? 'selected' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => open(t.id)}
              style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}
            >
              <div className="row-main">
                <div className="flex center gap-sm">
                  <PriorityBadge p={t.priority} short />
                  <span className="row-title">{t.title}</span>
                </div>
                <TaskMeta t={t} />
              </div>
              <span className="mono small muted">{t.ref}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
